import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [email, setEmail] = useState('');

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    // Handle newsletter signup
    Swal.fire({
      icon: 'success',
      title: 'Subscribed!',
      text: 'Thank you for subscribing! We\'ll keep you updated on strategic insights.',
      confirmButtonColor: '#2563eb'
    });
    setEmail('');
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-secondary-900 text-white overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-pattern opacity-5"></div>

      {/* Main Footer Content */}
      <div className="relative container-max section-padding">
        {/* Newsletter Section - Full Width */}
        <div className="bg-secondary-800 backdrop-blur-sm rounded-2xl p-6 sm:p-8 mb-12 border border-secondary-700">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce">
              <i className="bi bi-envelope text-2xl text-white"></i>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-2">Stay Ahead of the Curve</h3>
            <p className="text-secondary-200 mb-6 text-sm sm:text-base">
              Get exclusive insights, research updates, and strategic analysis delivered to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-secondary-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 backdrop-blur-sm text-sm sm:text-base"
              />
              <button
                type="submit"
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-primary-500/25 text-sm sm:text-base whitespace-nowrap"
              >
                Subscribe
              </button>
            </form>
            <p className="text-xs text-secondary-400 mt-3">Join 1,000+ strategic leaders. Unsubscribe anytime.</p>
          </div>
        </div>

        {/* Main Footer Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-12 mb-12">
          {/* Company Info - Takes 4 columns on large screens */}
          <div className="sm:col-span-2 lg:col-span-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/20 overflow-hidden">
                <img
                  src="https://res.cloudinary.com/dzj1fusx1/image/upload/v1768166080/2999d25d-09bd-49c1-8af2-a0c12acc1734.png"
                  alt="Kehosa Institute for Strategic Studies"
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-white">
                  Kehosa Institute for Strategic Studies
                </h3>
                <p className="text-xs sm:text-sm text-secondary-300 font-medium">KISS • Strategy Is the Foundation of Success</p>
              </div>
            </div>

            <p className="text-secondary-200 mb-6 leading-relaxed text-sm sm:text-base">
              Leading strategic thinking in Kenyan politics and democratic processes.
              Empowering leaders with evidence-based strategies for electoral success.
            </p>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-3 mb-6">
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1">
                <i className="bi bi-shield-check text-green-400 text-sm"></i>
                <span className="text-xs font-medium">Certified Excellence</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1">
                <i className="bi bi-star-fill text-yellow-400 text-sm"></i>
                <span className="text-xs font-medium">4.9/5 Rating</span>
              </div>
            </div>

            {/* Enhanced Social Media Links */}
            <div className="flex space-x-4">
              {[
                { icon: "twitter", url: "#", label: "Twitter" },
                { icon: "linkedin", url: "#", label: "LinkedIn" },
                { icon: "instagram", url: "#", label: "Instagram" },
                { icon: "youtube", url: "#", label: "YouTube" },
                { icon: "facebook", url: "#", label: "Facebook" }
              ].map((social) => (
                <a
                  key={social.icon}
                  href={social.url}
                  className="w-10 h-10 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 hover:shadow-lg group"
                  aria-label={social.label}
                >
                  <i className={`bi bi-${social.icon} text-secondary-300 group-hover:text-white transition-colors text-lg`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Programs - 2 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-bold mb-4 text-primary-400">
              Our Programs
            </h4>
            <ul className="space-y-3">
              <li>
                <Link to="/services" className="text-secondary-300 hover:text-white transition-colors duration-200 text-sm sm:text-base flex items-center group">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 group-hover:bg-accent-500 transition-colors"></span>
                  10 Keys to Victory™
                </Link>
              </li>
              <li>
                <Link to="/resources" className="text-secondary-300 hover:text-white transition-colors duration-200 text-sm sm:text-base flex items-center group">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 group-hover:bg-accent-500 transition-colors"></span>
                  DI-KISS Masterclasses
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-secondary-300 hover:text-white transition-colors duration-200 text-sm sm:text-base flex items-center group">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 group-hover:bg-accent-500 transition-colors"></span>
                  Kenya Democracy Project
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-secondary-300 hover:text-white transition-colors duration-200 text-sm sm:text-base flex items-center group">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-3 group-hover:bg-accent-500 transition-colors"></span>
                  About KISS
                </Link>
              </li>
            </ul>
          </div>

          {/* Masterclasses - 2 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-bold mb-4 text-accent-400">
              Masterclasses
            </h4>
            <ul className="space-y-3">
              <li className="text-secondary-300 hover:text-white transition-colors duration-200 text-xs sm:text-sm cursor-pointer">
                Winning Campaign Strategy
              </li>
              <li className="text-secondary-300 hover:text-white transition-colors duration-200 text-xs sm:text-sm cursor-pointer">
                Persuasive Advertising
              </li>
              <li className="text-secondary-300 hover:text-white transition-colors duration-200 text-xs sm:text-sm cursor-pointer">
                Media Interview Excellence
              </li>
              <li className="text-secondary-300 hover:text-white transition-colors duration-200 text-xs sm:text-sm cursor-pointer">
                Voter Mobilization
              </li>
              <li className="text-secondary-300 hover:text-white transition-colors duration-200 text-xs sm:text-sm cursor-pointer">
                Opponent Attack Survival
              </li>
            </ul>
          </div>

          {/* Contact Info - 2 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-bold mb-4 text-secondary-300">
              Connect With Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-envelope text-primary-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-secondary-300 text-sm font-medium">Email</p>
                  <a href="mailto:kehosainstitute@gmail.com" className="text-white hover:text-primary-400 transition-colors text-sm">
                    kehosainstitute@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-accent-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-telephone text-accent-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-secondary-300 text-sm font-medium">Phone</p>
                  <a href="tel:+254722655911" className="text-white hover:text-accent-400 transition-colors text-sm">
                    +254 722 655911
                  </a>
                </div>
              </div>

              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-secondary-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="bi bi-geo-alt text-secondary-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-secondary-300 text-sm font-medium">Location</p>
                  <p className="text-white text-sm">Nairobi, Kenya</p>
                </div>
              </div>
            </div>
          </div>

          {/* Partnerships - 2 columns */}
          <div className="lg:col-span-2">
            <h4 className="text-lg font-bold mb-4 text-white">
              Our Partners
            </h4>
            <div className="space-y-3">
              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                <h5 className="font-semibold text-primary-400 text-sm mb-1">Democracy Institute (DI)</h5>
                <p className="text-secondary-400 text-xs">Democratic governance & research</p>
              </div>

              <div className="bg-white/5 backdrop-blur-sm rounded-lg p-3 border border-white/10">
                <h5 className="font-semibold text-accent-400 text-sm mb-1">iTarget Media EA</h5>
                <p className="text-secondary-400 text-xs">Digital strategy & media solutions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-secondary-700 pt-8">
          <div className="flex flex-col lg:flex-row justify-between items-center gap-6">
            {/* Copyright */}
            <div className="text-center lg:text-left">
              <p className="text-secondary-400 text-sm">
                © {new Date().getFullYear()} Kehosa Institute for Strategic Studies (KISS).
                <span className="block sm:inline sm:ml-1">All rights reserved.</span>
              </p>
              <p className="text-secondary-500 text-xs mt-1">
                In partnership with Democracy Institute (DI) and iTarget Media EA.
              </p>
            </div>

            {/* Developer Credit & Legal Links */}
            <div className="flex flex-col lg:flex-row justify-between items-center gap-4 lg:gap-6">
              {/* Developer Credit */}
              <div className="text-center lg:text-left">
                <p className="text-secondary-400 text-xs">
                  Website developed by{' '}
                  <a
                    href="https://www.nexusgk.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-400 hover:text-primary-300 transition-colors font-medium"
                  >
                    NexusGK Developer
                  </a>
                </p>
              </div>

              {/* Legal Links */}
              <div className="flex flex-wrap justify-center lg:justify-end gap-4 sm:gap-6">
                <a href="#" className="text-secondary-400 hover:text-white transition-colors text-sm">Privacy Policy</a>
                <a href="#" className="text-secondary-400 hover:text-white transition-colors text-sm">Terms of Service</a>
                <a href="#" className="text-secondary-400 hover:text-white transition-colors text-sm">Code of Ethics</a>
                <a href="#" className="text-secondary-400 hover:text-white transition-colors text-sm">Research Ethics</a>
              </div>
            </div>

            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className="fixed bottom-6 right-6 w-12 h-12 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 flex items-center justify-center z-50"
              aria-label="Back to top"
            >
              <i className="bi bi-arrow-up text-lg"></i>
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;